import { createSelector } from "reselect"
import { get, chain, map, forEach, find, keyBy, countBy } from "lodash"
import pluralize from "pluralize"
import { getContentSets } from "selectors/contentSets"
import { COLORS } from "helpers/analyzeConstants"
import { getMustAggregateFactors, getStyleExposureAggregation } from "modules/portfolio-root/selectors/portfolios"
import { aggregateExposuresFactors } from "helpers/factorClassification/index"
import { getTopFactor } from "selectors/factors"
import { getModelFactors, getModelCategories, getModelFactorsClassification } from "selectors/modelMetadata"

const EMPTY_ARRAY = []

const getUserSelectedMetric = state => get(state, "modules.compareExposure.selectedMetric")
const getSavedMetric = (_, props) => get(props, "savedMetric")
const getContentSetId = (_, props) => get(props, "contentSetId")

export const getFactorsMetadata = createSelector(
  [getModelFactors, getMustAggregateFactors, getStyleExposureAggregation, getModelFactorsClassification],
  (factorsMetadata, mustAggregateFactors, styleAggregation, factorsClassification) => {
    if (!mustAggregateFactors) {
      return factorsMetadata
    }

    const factors = aggregateExposuresFactors(factorsClassification, factorsMetadata, styleAggregation)
    return keyBy(factors, "id")
  },
)

export const getRawFactorsCategories = createSelector(
  [getModelCategories, getContentSets, getContentSetId],
  (categories, contentSets, contentSetId) => {
    if (contentSetId) {
      const contentSet = find(contentSets, ({ id }) => id === contentSetId)
      if (contentSet) {
        const factorsCountPerCategory = countBy(contentSet.factors, "category")
        return map(contentSet.categories, ({ id, name }, index) => ({
          id,
          label: name,
          color: COLORS[index],
          singleFactor: factorsCountPerCategory[id] === 1,
        }))
      }
      return null
    }

    return categories
  },
)

export const getFactors = createSelector(
  [getFactorsMetadata, getContentSets, getContentSetId],
  (factors, contentSets, contentSetId) => {
    if (contentSetId) {
      const contentSet = find(contentSets, ({ id }) => id === contentSetId)
      if (contentSet) {
        return chain(contentSet.factors)
          .keyBy("id")
          .mapValues(({ name, category }) => ({
            category, label: name,
          }))
          .value()
      }
      return null
    }

    return factors
  },
)

function getCategory(category, factors) {
  if (category.singleFactor) {
    return {
      label: category.label,
      options: chain(factors)
        .pickBy(factor => factor.category === category.id)
        .map((factor, id) => ({
          label: factor.label,
          id,
          value: item =>
            chain(item)
              .get("factors", EMPTY_ARRAY)
              .find(factor => factor.id === id)
              .get("net", 0)
              .value(),
        }))
        .value(),
    }
  } else {
    return {
      label: pluralize(category.label),
      options: chain(factors)
        .pickBy(factor => factor.category === category.id)
        .map((factor, id) => ({
          label: factor.label,
          id,
          value: item => chain(item)
            .get("factors", EMPTY_ARRAY)
            .find(factor => factor.id === id)
            .get("net", 0)
            .value(),
        }))
        .value(),
    }
  }
}

export const getMetrics = createSelector(
  [getFactors, getRawFactorsCategories],
  (factors, categories) => map(categories, category => getCategory(category, factors)),
)

export const getSelectedMetric = createSelector(
  [getMetrics, getUserSelectedMetric, getSavedMetric, getTopFactor],
  (metrics, selectedMetric, savedMetric, topFactor) => {
    let metricData = null

    const metric = selectedMetric || savedMetric || topFactor

    forEach(metrics, item => {
      const data = find(item.options, { id: metric })

      if (data) {
        metricData = data
        return false
      }
    })

    if (!metricData) {
      metricData = get(metrics, "0.options.0", {})
    }
    return metricData
  },
)
